import { addDays } from "date-fns"
import { min, sortBy, uniqBy } from "lodash"

import { outOfStockDate } from "~/components/planning/ProductTable/columns/stock"
import type { BadgeProps } from "~/components/design-system/components"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { DateFormatType, formatDate, startOfTodayUTC } from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"
import { availabilityOf } from "~/utils/stockProjection/engine"

export type TriggerReason =
  | "stockoutBeforeDelivery"
  | "delayedPo"
  | "belowSafetyStock"
  | "highLostSales"
  | "runsLowBeforeNextBuy"

// Ordered by urgency; pills and filter chips render in this order.
export const TRIGGER_REASONS: {
  id: TriggerReason
  label: string
  description: string
  badgeVariant: BadgeProps["variant"]
}[] = [
  {
    id: "stockoutBeforeDelivery",
    label: "Stockout before delivery",
    description: "Runs out before an order placed today can land",
    badgeVariant: "red",
  },
  {
    id: "delayedPo",
    label: "Delayed PO",
    description: "Has an incoming PO, but it lands after stock runs out",
    badgeVariant: "red",
  },
  {
    id: "belowSafetyStock",
    label: "Below safety stock",
    description: "Drops below safety stock within lead time without running out",
    badgeVariant: "yellow",
  },
  {
    id: "highLostSales",
    label: `High lost ${formatName("sale", true)}`,
    description: "Availability within lead time is below 50%",
    badgeVariant: "yellow",
  },
  {
    id: "runsLowBeforeNextBuy",
    label: "Runs low before next buy",
    description:
      "Covered until an order placed today lands, but runs out or below safety stock before the next buy can land",
    badgeVariant: "neutral",
  },
]

const HIGH_LOST_SALES_AVAILABILITY = 0.5

export function triggerReasonLabel(id: TriggerReason): string {
  return TRIGGER_REASONS.find((r) => r.id === id)?.label ?? id
}

export function isTriggerReason(value: unknown): value is TriggerReason {
  return TRIGGER_REASONS.some((r) => r.id === value)
}

type ReasonVariant = Pick<
  IVariantSummary,
  "settings" | "outOfStockInformation" | "availabilityWithinLeadTime"
> &
  Partial<Pick<IVariantSummary, "stockOutInfoWarehouse">>

export type TriggerTiming = {
  // Earliest stockout across SKUs and warehouses a buy today can still affect; null if none in
  // the horizon. In the past (last day of stock) when a SKU is out and nothing refills it first.
  coverEnd: Date | null
  // Where the gap after `coverEnd` ends. A delayed PO landing earlier never ends it: a stockout it
  // refills before this date is already left out of `coverEnd`.
  orderTodayLands: Date
  // Latest order date that still lands before the stockout.
  orderBy: Date | null
  // Distinct SKUs already out today, so a variant isn't read as out when only one size is.
  skusOutNow: number
}

export type SkuStockout = {
  skuId: string
  // Where it runs dry; undefined when only the pooled stock does.
  warehouseId: string | undefined
  coverEnd: Date
}

type StockoutEntry = NonNullable<IVariantSummary["outOfStockInformation"][number]>

function stockoutEntries(variant: ReasonVariant) {
  // Suggestions are computed per warehouse, so a warehouse running dry triggers the variant even
  // when the pooled stock looks fine: read every warehouse's dates alongside the aggregate.
  return [
    ...(variant.outOfStockInformation ?? []),
    ...Object.values(variant.stockOutInfoWarehouse ?? {}).flat(),
  ]
}

// Stockouts an incoming PO refills before an order placed today can land are left out: a buy
// today can't change them. Selling that PO out again starts a new period, which still counts.
function uncoveredStockoutDates(entry: StockoutEntry, orderTodayLands: Date): Date[] {
  return (entry.stockoutDates ?? []).filter((start, i) => {
    // Same stockout weeks as `stockoutDates`, paired with the week stock comes back.
    const [startWeek, restockWeek] = entry.stockOutPeriods?.[i] ?? []
    if (startWeek === undefined || restockWeek === undefined) return true
    return addDays(start, (restockWeek - startWeek) * 7) > orderTodayLands
  })
}

// `safetyStockOutDate` is where stock reaches safety stock on its way to the entry's first stockout.
// When a PO refills that stockout before an order placed today lands, the breach goes with it, as
// the stockout does above. The next stockout a buy today can affect is breached the same span
// before it (exact for week-cover safety stock, close for a fixed buffer at a steady forecast).
function uncoveredSafetyStockOutDate(
  entry: StockoutEntry,
  orderTodayLands: Date,
): Date | undefined {
  const safety = entry.safetyStockOutDate
  const first = entry.stockoutDates?.[0]
  if (!safety || !first) return safety
  const [next] = uncoveredStockoutDates(entry, orderTodayLands)
  if (!next) return undefined
  if (next.getTime() === first.getTime()) return safety
  return new Date(next.getTime() - (first.getTime() - safety.getTime()))
}

function orderTodayLandsOf(variant: ReasonVariant, today: Date): Date {
  return addDays(today, variant.settings.totalLeadTimeDays)
}

// The variant's stockout dates as a buy today sees them; null when phased out.
function earliestStockout(
  variant: ReasonVariant,
  entries: (StockoutEntry | undefined)[],
): Date | null {
  return outOfStockDate({ ...variant, outOfStockInformation: entries } as IVariantSummary) ?? null
}

// Every SKU-warehouse that runs out before `before` and isn't refilled before an order placed
// today lands, earliest first. The variant row shows the worst of these.
export function getSkuStockouts(variant: ReasonVariant, before: Date, today: Date): SkuStockout[] {
  const orderTodayLands = orderTodayLandsOf(variant, today)
  const out: SkuStockout[] = []
  const collect = (entries: (StockoutEntry | undefined)[], warehouseId: string | undefined) => {
    for (const entry of entries) {
      const coverEnd = entry && min(uncoveredStockoutDates(entry, orderTodayLands))
      if (entry && coverEnd && coverEnd < before) {
        out.push({ skuId: entry.skuId, warehouseId, coverEnd })
      }
    }
  }
  for (const [warehouseId, entries] of Object.entries(variant.stockOutInfoWarehouse ?? {})) {
    collect(entries, warehouseId)
  }
  // The pooled stock only adds SKUs no single warehouse already accounts for.
  const listed = new Set(out.map((s) => s.skuId))
  collect(
    (variant.outOfStockInformation ?? []).filter((e) => e && !listed.has(e.skuId)),
    undefined,
  )
  return sortBy(out, (s) => s.coverEnd.getTime())
}

export function getTriggerTiming(variant: ReasonVariant, today: Date): TriggerTiming {
  const leadTimeDays = variant.settings.totalLeadTimeDays
  const orderTodayLands = orderTodayLandsOf(variant, today)
  const coverEnd = earliestStockout(
    variant,
    stockoutEntries(variant).map(
      (e) => e && { ...e, stockoutDates: uncoveredStockoutDates(e, orderTodayLands) },
    ),
  )
  return {
    coverEnd,
    orderTodayLands,
    orderBy: coverEnd ? addDays(coverEnd, -leadTimeDays) : null,
    skusOutNow: coverEnd ? uniqBy(getSkuStockouts(variant, today, today), "skuId").length : 0,
  }
}

// Lead times reach into next year, where "12 Apr" alone reads as this year.
export function formatTriggerDate(date: Date, today: Date = startOfTodayUTC()): string {
  return date.getUTCFullYear() === today.getUTCFullYear()
    ? formatDate(date, DateFormatType.DayMonth)
    : formatDate(date, DateFormatType.FullDateShort)
}

export function isOutOfStockNow(timing: Pick<TriggerTiming, "coverEnd">, today: Date): boolean {
  return timing.coverEnd != null && timing.coverEnd < today
}

// Share of lead-time demand served; null without demand.
export function availabilityWithinLeadTime(
  variant: Pick<IVariantSummary, "availabilityWithinLeadTime">,
): number | null {
  const availability = availabilityOf(
    variant.availabilityWithinLeadTime ? [variant.availabilityWithinLeadTime] : [],
  )
  return availability.kind === "ratio" ? availability.value : null
}

export function getTriggerReasons(
  variant: ReasonVariant,
  timing: Pick<TriggerTiming, "coverEnd" | "orderTodayLands">,
  delayedPo: { poExpectedArrivalDate: Date } | undefined,
  purchaseIntervalDays: number,
): TriggerReason[] {
  const reasons: TriggerReason[] = []
  const entries = stockoutEntries(variant)
  const { coverEnd, orderTodayLands } = timing

  const stocksOutFirst = coverEnd != null && coverEnd < orderTodayLands
  if (stocksOutFirst) reasons.push("stockoutBeforeDelivery")

  // The PO itself being late is the news here, so read the stockout it was meant to prevent.
  const firstStockout = earliestStockout(variant, entries)
  if (
    delayedPo &&
    firstStockout != null &&
    firstStockout < new Date(delayedPo.poExpectedArrivalDate)
  ) {
    reasons.push("delayedPo")
  }

  const safetyStockOut = min(
    entries.map((o) => o && uncoveredSafetyStockOutDate(o, orderTodayLands)),
  )
  const breachesSafetyFirst = safetyStockOut != null && safetyStockOut < orderTodayLands
  if (!stocksOutFirst && breachesSafetyFirst) reasons.push("belowSafetyStock")

  // The buy placed today has to hold until the next one can land.
  const nextBuyLands = addDays(orderTodayLands, purchaseIntervalDays)
  const runsLowThisCycle =
    (coverEnd != null && coverEnd < nextBuyLands) ||
    (safetyStockOut != null && safetyStockOut < nextBuyLands)
  if (!stocksOutFirst && !breachesSafetyFirst && runsLowThisCycle) {
    reasons.push("runsLowBeforeNextBuy")
  }

  const availability = availabilityWithinLeadTime(variant)
  if (availability != null && availability < HIGH_LOST_SALES_AVAILABILITY) {
    reasons.push("highLostSales")
  }

  return reasons
}
